import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset, CheckCircle } from '@mui/icons-material';
import AuthLayout from '../../components/auth/AuthLayout';
import { validateResetToken, resetPassword, clearError } from '../../redux/slices/authSlice';

// Validation schema
const ResetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

const ResetPasswordPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenValidating, setTokenValidating] = useState(true);
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error } = useSelector((state) => state.auth);
  
  // Get token and uid from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const uid = queryParams.get('uid');
  
  // Debug: Log token and uid
  console.log('Reset Password - Token:', token);
  console.log('Reset Password - UID:', uid);

  useEffect(() => {
    const validateToken = async () => {
      if (token && uid) {
        try {
          // Pass both token and uid to the validation function
          const result = await dispatch(validateResetToken({ token, uid }));
          console.log('Token validation result:', result);
          if (result.meta.requestStatus === 'fulfilled') {
            setTokenValid(true);
          } else {
            setTokenValid(false);
          }
        } catch (error) {
          console.error('Token validation error:', error);
          setTokenValid(false);
        } finally {
          setTokenValidating(false);
        }
      } else {
        console.log('Missing token or uid:', { token, uid });
        setTokenValid(false);
        setTokenValidating(false);
      }
    };

    validateToken();
  }, [dispatch, token, uid]);

  const handleSubmit = async (values) => {
    if (token && uid) {
      const result = await dispatch(resetPassword({
        token,
        uid,
        password: values.password
      }));
      
      if (result.meta.requestStatus === 'fulfilled') {
        setResetSuccess(true);
      }
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (tokenValidating) {
    return (
      <AuthLayout>
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
            }}
          >
            <Card
              sx={{
                width: '100%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
              }}
            >
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h6">Validating reset token...</Typography>
            </Card>
          </Box>
        </Container>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            py: 4,
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              textAlign: 'center',
              mb: 4,
              animation: 'fadeIn 0.6s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(-20px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mb: 2,
                boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
              }}
            >
              <LockReset sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ fontSize: '1.1rem', fontWeight: 400 }}
            >
              Choose a new password for your account
            </Typography>
          </Box>

          <Card
            sx={{
              width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3 }}
                  onClose={() => dispatch(clearError())}
                >
                  {typeof error === 'object'
                    ? (error.detail || error.message || error.messages || JSON.stringify(error))
                    : error}
                </Alert>
              )}

              {!tokenValid && !tokenValidating ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Alert severity="error" sx={{ mb: 3 }}>
                    Invalid or expired password reset token.
                  </Alert>
                  <Typography variant="body1" paragraph>
                    The password reset link is invalid or has expired. Please request a new password reset link.
                  </Typography>
                  <Button
                    component={Link}
                    to="/auth/forgot-password"
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                  >
                    Request New Reset Link
                  </Button>
                </Box>
              ) : resetSuccess ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 3,
                    animation: 'fadeIn 0.5s ease-in',
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: '#d4edda',
                      mb: 2,
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 40, color: '#28a745' }} />
                  </Box>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 600, color: '#28a745', mb: 2 }}
                  >
                    Password Reset Successful!
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
                    Your password has been reset successfully. You can now log in with your new password.
                  </Typography>
                  <Button
                    component={Link}
                    to="/auth/login"
                    variant="contained"
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
                        boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                      },
                    }}
                  >
                    Go to Login
                  </Button>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ mb: 3, textAlign: 'center' }}
                  >
                    Please enter your new password below. Make sure it's strong and secure.
                  </Typography>

                  <Formik
                    initialValues={{ password: '', confirmPassword: '' }}
                    validationSchema={ResetPasswordSchema}
                    onSubmit={handleSubmit}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      isSubmitting,
                    }) => (
                      <Form>
                        <TextField
                          fullWidth
                          id="password"
                          name="password"
                          label="New Password"
                          type={showPassword ? 'text' : 'password'}
                          variant="outlined"
                          margin="normal"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          disabled={isLoading}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleTogglePasswordVisibility}
                                  edge="end"
                                  disabled={isLoading}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />

                        <TextField
                          fullWidth
                          id="confirmPassword"
                          name="confirmPassword"
                          label="Confirm New Password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          variant="outlined"
                          margin="normal"
                          value={values.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                          helperText={touched.confirmPassword && errors.confirmPassword}
                          disabled={isLoading}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          sx={{
                            mb: 1,
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle confirm password visibility"
                                  onClick={handleToggleConfirmPasswordVisibility}
                                  edge="end"
                                  disabled={isLoading}
                                >
                                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />

                        <Box sx={{ mt: 4, mb: 2 }}>
                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={isLoading}
                            sx={{
                              py: 1.5,
                              fontSize: '1rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                                transform: 'translateY(-2px)',
                              },
                              '&:active': {
                                transform: 'translateY(0)',
                              },
                              '&:disabled': {
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                opacity: 0.7,
                              },
                            }}
                          >
                            {isLoading ? (
                              <CircularProgress size={24} color="inherit" />
                            ) : (
                              'Reset Password'
                            )}
                          </Button>
                        </Box>

                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                          <Link
                            to="/auth/login"
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography
                              color="primary"
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                transition: 'opacity 0.2s',
                                '&:hover': {
                                  opacity: 0.8,
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              Back to Login
                            </Typography>
                          </Link>
                        </Box>
                      </Form>
                    )}
                  </Formik>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>
    </AuthLayout>
  );
};

export default ResetPasswordPage;